import React from 'react';
import { useHistory } from 'react-router-dom';
import useAppContext from '../../../../context/useAppContext';
import {
    CheckboxLabel,
    ToastContext,
    Icon,
    Form,
    Input,
    Button,
    Message,
    Section,
    FrameCard,
    WeezHeader,
    InputLabel,
    TooltipHelp,
    ToggleLabel,
    Select,
    SelectLabel,
    Counter,
    CounterLabel,
    OptionalForm,
    SplitContainer,
    ToggleContainer,
    TranslateDropdown,
    Modal
} from '@weezevent/nacre';
import { Category, Zone as ZoneModel } from '../../../../models';
import { useForm, fetcher } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import { UploadDropZone } from '../../../../nacre/uploadButton';
import UploadFileZone from '../../../../components/upload/uploadFileZone';
import { UploadPreviewFile } from '../../../../components/upload/components/uploadPreview';
import LoadingCard from '../../../../components/loadingCard';
import TypeSelector from './components/TypeSelector';
import SeatMapModal from './components/seatmap/SeatMapModal';
import ZoneEditorModal from '../chart_custom/zoneEditor/zoneEditorModal';
import DeletionModal from '../../../../components/deletionModal';
import { ButtonZoneEditorAccess } from '../chart_custom/chart';
import css from './zones.module.css';
import { LANGUAGES } from '../../../../components/translations/translationModal';
import Contingent from '../../../../models/contingentModel';
import UpdateZoneModal from './components/UpdateZoneModal';

export default function Zone({ chartId, zoneId }) {
    const [{ organization, chart }] = useAppContext();
    const { t } = useTranslation();
    const history = useHistory();
    const [submitting, setSubmitting] = React.useState(false);
    const [currentCategory, setCurrentCategory] = React.useState(null);
    const [confirmationSubmit, setConfirmationSubmit] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const rule = /[^A-Za-z0-9\sàâçéèêëîïôûùüÿñæœ.-]+/g;

    const languageOptions = React.useMemo(() => {
        return organization.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization]);

    const defaultLanguage = React.useMemo(() => {
        let defaultLocale = languageOptions.find(it => it.value.includes(organization.default_locale.replace('_', '-').toLowerCase()));
        return defaultLocale?.value || languageOptions[0]?.value;
    }, [organization, languageOptions]);

    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);
    const [selectedDescriptionLanguage, setSelectedDescriptionLanguage] = React.useState(defaultLanguage);

    const [form, setForm, setFormObject] = useForm({
        name: '',
        access: '',
        capacityGA: null,
        type: 'general_admission',
        description: '',
        image: null,
        parent: null,
        category: null,
        contingent: null,
        automatic_seat_allocation_strategy: 'default_from_bottom',
        seatMap: [],
        zoneMap: [],
        translations: {},
        seatMapRejected: null,
        scene_location: 'DOWN',
        seat_count: 0,
        display_order: 0,
        deletingImage: null,
        display_stage: false,
        general_admission: null,
        preview_picture_id: null,
        zone_representation: false,
        translationsDescription: {},
        seatlib_data: {
            display_category_colors: false,
            display_unavailable: true,
            show_stage: false
        }
    });

    const parseBool = React.useCallback(value => {
        return value === true || value === 'true';
    });

    const [categories, loadingCategories, syncCategories] = Category.useApiModel(
        {
            organizationId: organization.id,
            chartId
        },
        [organization, chartId]
    );
    const [contingents, loadingContingents, syncContingents] = Contingent.useApiModel(
        {
            organizationId: organization.id,
            chartId
        },
        [organization, chartId]
    );

    const [zones, loadingzones, synczones] = ZoneModel.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            query: {
                withSeats: true
            },
            cache: false,
            onSuccess: _zones => {
                if (zoneId) {
                    let trans = {};
                    let transDecs = {};
                    const zone = _zones.find(z => z.uuid === zoneId);
                    trans[defaultLanguage] = zone.name;

                    if ('name' in zone.translations) {
                        for (let [key, value] of Object.entries(zone.translations.name)) {
                            trans[key] = value;
                        }
                    }
                    if (zone.description) {
                        transDecs[defaultLanguage] = zone.description;
                    }
                    if ('description' in zone.translations) {
                        for (let [key, value] of Object.entries(zone.translations.description)) {
                            transDecs[key] = value;
                        }
                    }

                    setFormObject({
                        ...form,
                        zone: zone,
                        name: zone.name,
                        description: zone.description,
                        display_order: zone.display_order ? zone.display_order : 0,
                        type: zone.general_admission === null ? 'seated' : 'general_admission',
                        parent: zone.parent,
                        category: zone.general_admission !== null ? zone.general_admission.category : null,
                        automatic_seat_allocation_strategy: zone.general_admission === null ? zone.automatic_seat_allocation_strategy : null,
                        capacityGA: zone.general_admission !== null ? zone.general_admission.capacity : 0,
                        // Pyvar returns JsonField boolean values as strings, so datas for seatlib_data are enforced here.
                        seatlib_data: {
                            ...zone.seatlib_data,
                            show_stage: parseBool(zone.seatlib_data?.show_stage),
                            display_category_colors: parseBool(zone.seatlib_data?.display_category_colors),
                            display_unavailable: parseBool(zone.seatlib_data?.display_unavailable)
                        },
                        scene_location: zone.scene_location,
                        preview_picture_id: zone.preview_picture?.uuid,
                        image: zone.preview_picture?.url,
                        seat_count: zone.seat_count,
                        zone_representation: Boolean(zone.preview_picture),
                        translations: trans,
                        translationsDescription: transDecs
                    });
                    setCurrentCategory(zone.general_admission?.category);
                }
            }
        },
        [organization, chartId, zoneId, setFormObject, defaultLanguage]
    );

    const inputValidity = React.useCallback(
        entry => {
            if (entry?.match(new RegExp(rule))) {
                return false;
            }
            return true;
        },
        [rule]
    );

    const inputError = React.useCallback(
        item => {
            if (!item || !item?.match(new RegExp(rule))) {
                return null;
            }
            return t('ticket.charts.zone.input_error');
        },
        [rule]
    );

    const editMode = React.useMemo(() => {
        return Boolean(zoneId);
    }, [zoneId]);

    const handleBack = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/zones`);
    }, [organization, chartId]);

    const handleReset = React.useCallback(() => {
        syncCategories();
        syncContingents();
        synczones();
    }, [syncCategories, syncContingents, synczones]);

    const handleType = React.useCallback(
        value => {
            setFormObject({ ...form, type: value, category: null });
        },
        [setFormObject, form]
    );

    const handleHeaderCancel = React.useCallback(() => {
        if (!editMode) {
            return handleBack();
        }
        return handleReset();
    }, [editMode, handleBack, handleReset]);

    const isGA = React.useMemo(() => {
        return form.type === 'general_admission';
    }, [form]);

    const hasNameTranslations = React.useMemo(() => {
        if (!Object.keys(form.translations)) {
            return false;
        }
        return defaultLanguage in form.translations && Object.keys(form.translations).length > 1;
    }, [form, form.translations, defaultLanguage]);

    const hasDescriptionTranslations = React.useMemo(() => {
        if (!Object.keys(form.translationsDescription)) {
            return false;
        }
        return defaultLanguage in form.translationsDescription && Object.keys(form.translationsDescription).length > 1;
    }, [defaultLanguage, form, form.translationsDescription]);

    const disableSubmit = React.useMemo(() => {
        if (editMode) {
            return !inputValidity(form.access);
        }

        if (!isGA) {
            if (!form.seatMap.length) {
                return !form.name || !inputValidity(form.access);
            }
            return !form.category || !form.name || !form.access || !inputValidity(form.access);
        }

        return !form.category || (form.capacityGA !== null && form.capacityGA < 1) || !form.name || !inputValidity(form.access);
    }, [form, isGA, editMode]);

    const getSeatsCount = React.useMemo(() => {
        if (!editMode || !form.zone) {
            return null;
        }
        let count = 0;
        let childrens = zones?.filter(z => z.parent === form.zone?.uuid);

        if (!childrens.length) {
            return form.zone.seats.length || form.zone.general_admission?.capacity || 0;
        }

        if (childrens.length) {
            childrens.map(zn => {
                if (zn.seats.length) {
                    return (count += parseInt(zn.seats.length));
                } else if (zn.general_admission) {
                    return (count += parseInt(zn.general_admission?.capacity));
                } else {
                    return (count += parseInt(zn.seat_count));
                }
            });
            return count;
        }
    }, [form, zones]);

    const GaData = React.useMemo(() => {
        let data = {
            name: form.name,
            parent: form.parent || null,
            description: form.description?.length ? form.description : null,
            translations: {},
            display_order: parseInt(form.display_order),
            seatlib_data: {
                ...form.seatlib_data,
                display_unavailable: form.seatlib_data.display_unavailable,
                show_stage: form.seatlib_data.show_stage
            },
            scene_location: form.scene_location,
            general_admission: {
                category: form.category,
                name: form.name,
                capacity: form.capacityGA
            }
        };
        if (hasNameTranslations && !hasDescriptionTranslations) {
            const translations = { name: form.translations };
            data.translations = translations;
        }
        if (hasDescriptionTranslations && !hasNameTranslations) {
            const descriptions = { description: form.translationsDescription };
            data.translations = descriptions;
        }

        if (hasNameTranslations && hasDescriptionTranslations) {
            const translations = { name: form.translations, description: form.translationsDescription };
            data.translations = translations;
        }
        data.preview_picture_id = null;

        if (form.image) {
            data.image = form.image;
            data.preview_picture_id = form.preview_picture_id;
        }
        return data;
    }, [form, form.translations, isGA, hasNameTranslations, hasDescriptionTranslations, zoneId]);

    const payload = React.useMemo(() => {
        if (!editMode) {
            let formData = new FormData();
            if (!isGA) {
                formData.append('file', form.seatMap[0] || null);
                formData.append('name', form.name);
                formData.append('scene_location', form.scene_location);

                formData.append('access', form.access);
                formData.append('category', form.category);
                formData.append('display_order', form.display_order);
                formData.append('description', form.description?.length ? form.description : '');
                formData.append('preview_picture_id', '');

                if (form.contingent) {
                    formData.append('contingent', form.contingent);
                }

                if (form.automatic_seat_allocation_strategy) {
                    formData.append('automatic_seat_allocation_strategy', form.automatic_seat_allocation_strategy);
                }

                if (form.parent) {
                    formData.append('parent', form.parent);
                }

                if (form.image) {
                    formData.append('image', form.image);
                    formData.append('preview_picture_id', form.preview_picture_id);
                }

                if (hasNameTranslations && !hasDescriptionTranslations) {
                    const translations = JSON.stringify({ name: form.translations });
                    formData.append('translations', translations);
                }
                if (hasDescriptionTranslations && !hasNameTranslations) {
                    const descriptions = JSON.stringify({ description: form.translationsDescription });
                    formData.append('translations', descriptions);
                }
                if (hasNameTranslations && hasDescriptionTranslations) {
                    const translations = JSON.stringify({ name: form.translations, description: form.translationsDescription });
                    formData.append('translations', translations);
                }
                if (form.seatlib_data) {
                    for (const [key, value] of Object.entries(form.seatlib_data)) {
                        formData.append(`seatlib_data.${key}`, JSON.stringify(value));
                    }
                }
                return formData;
            }
            return GaData;
        } else {
            let formData = new ZoneModel();
            formData.uuid = zoneId;
            if (isGA) {
                formData.general_admission = {
                    category: form.category,
                    name: form.name,
                    capacity: form.capacityGA
                };
            }

            formData.file = form.seatMap[0] || null;
            formData.name = form.name;
            formData.scene_location = form.scene_location;
            formData.parent = form.parent;
            formData.access = form.access;
            formData.category = form.category;
            formData.contingent = form.contingent;
            formData.display_order = form.display_order;
            formData.description = form.description?.length ? form.description : null;
            formData.preview_picture_id = '';

            if (form.image) {
                formData.image = form.image;
                formData.preview_picture_id = form.preview_picture_id;
            }

            if (hasNameTranslations && !hasDescriptionTranslations) {
                const translations = { name: form.translations };
                formData.translations = translations;
            }
            if (hasDescriptionTranslations && !hasNameTranslations) {
                const descriptions = { description: form.translationsDescription };
                formData.translations = descriptions;
            }

            if (hasNameTranslations && hasDescriptionTranslations) {
                const translations = { name: form.translations, description: form.translationsDescription };
                formData.translations = translations;
            }
            if (form.seatlib_data) {
                formData.seatlib_data = form.seatlib_data;
            }
            if (form.automatic_seat_allocation_strategy) {
                formData.automatic_seat_allocation_strategy = form.automatic_seat_allocation_strategy;
            }
            return formData;
        }
    }, [form, form.translations, isGA, hasNameTranslations, hasDescriptionTranslations, zoneId, GaData]);

    const handleSubmit = React.useCallback(() => {
        // When creating a seated zone, it's mandatory to use FormData to send .xlsx files to Pyvar.
        // But not in an edition case, hence the switch between fetcher.post and useApiModel.save()
        setSubmitting(true);
        if (editMode) {
            payload
                .save({
                    organizationId: organization.pk,
                    chartId
                })
                .then(() => {
                    toast.success(t('ticket.charts.newZone.editSuccess'));
                    handleBack();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.charts.newZone.editFailure'));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        } else {
            fetcher
                .post(`/ticket/organizations/${organization.id}/charts/${chartId}/zones`, {
                    data: payload
                })
                .then(() => {
                    toast.success(t('ticket.charts.newZone.success'));
                    handleBack();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.charts.newZone.failure'));
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    }, [organization, chartId, payload, handleBack]);

    return (
        <>
            <Modal
                open={Boolean(confirmationSubmit)}
                allowExternalClosing={false}
                onClose={() => setConfirmationSubmit(false)}
                submitButton={<Button primary label={t('ticket.common.action.save')} onClick={handleSubmit} />}
                cancelButton={<Button onClick={() => setConfirmationSubmit(false)} label={t('ticket.common.action.close')} />}
                title={t('ticket.charts.newZone.modale-title')}
                subtitle={t('ticket.charts.newZone.modale-subtitle')}
            />
            <WeezHeader
                backLink={{
                    onClick: () => handleBack()
                }}
                rightComponent={[
                    <>
                        {editMode && !loadingzones && (
                            <span className={css['seats-count-label']}>
                                <Icon name={form.type === 'seated' ? 'seat' : 'male'} /> {`${t('ticket.charts.zone.seats')} ${getSeatsCount}`}
                            </span>
                        )}
                        <Button
                            key="Delete"
                            className={css['zone-reset-btn']}
                            onClick={handleHeaderCancel}
                            label={
                                editMode ? (
                                    <>
                                        <Icon name="Reset" /> <span>{t('ticket.common.action.reset')}</span>
                                    </>
                                ) : (
                                    t('ticket.common.action.cancel')
                                )
                            }
                        />
                        <Button
                            key="Display"
                            className={css['zone-save-btn']}
                            label={
                                <>
                                    <Icon className={css['zone-save-mobile-icon']} name="save" size="regular" />
                                    <span className={css['zone-save-mobile-label']}>{t('ticket.common.action.save')}</span>
                                </>
                            }
                            disabled={disableSubmit || submitting}
                            onClick={() => (currentCategory && currentCategory !== form.category ? setConfirmationSubmit(true) : handleSubmit())}
                            primary
                        />
                    </>
                ]}
                title={editMode ? form.name : t('ticket.charts.chart.zones_add')}
            />

            <SplitContainer
                additional={
                    <Message type="tips" title={t('notifications.tips_title')} mini>
                        <p>{t('ticket.charts.newZone.tips')}</p>
                    </Message>
                }
            >
                <ConfigFrame
                    form={form}
                    isGA={isGA}
                    chart={chart}
                    zoneId={zoneId}
                    setForm={setForm}
                    chartId={chartId}
                    editMode={editMode}
                    synczones={synczones}
                    inputError={inputError}
                    categories={categories}
                    contingents={contingents}
                    handleType={handleType}
                    submitting={submitting}
                    loadingzones={loadingzones}
                    organization={organization}
                    inputValidity={inputValidity}
                    setFormObject={setFormObject}
                    loadingCategories={loadingCategories}
                    loadingContingents={loadingContingents}
                />

                <GeneralFrame
                    form={form}
                    isGA={isGA}
                    zones={zones}
                    chart={chart}
                    setForm={setForm}
                    editMode={editMode}
                    inputError={inputError}
                    submitting={submitting}
                    loadingzones={loadingzones}
                    organization={organization}
                    setFormObject={setFormObject}
                    inputValidity={inputValidity}
                    defaultLanguage={defaultLanguage}
                    languageOptions={languageOptions}
                    selectedNameLanguage={selectedNameLanguage}
                    setSelectedNameLanguage={setSelectedNameLanguage}
                    selectedDescriptionLanguage={selectedDescriptionLanguage}
                    setSelectedDescriptionLanguage={setSelectedDescriptionLanguage}
                    zoneId={zoneId}
                />
                {chart && !chart.pacifa_key && <OptionalFrame form={form} setForm={setForm} submitting={submitting} setFormObject={setFormObject} />}
            </SplitContainer>
        </>
    );
}

export function ConfigFrame({ ...props }) {
    const { t } = useTranslation();
    const { editMode, isGA, form, setFormObject, setForm } = props;

    const handleSeatMap = React.useCallback(
        (rejected, files) => {
            if (rejected.length) {
                return setForm('seatMapRejected', rejected[0]);
            }
            return setFormObject({ ...form, seatMap: [files], seatMapRejected: null });
        },
        [setFormObject, setForm, form]
    );

    const seatMapError = React.useMemo(() => {
        if (!form.seatMapRejected) {
            return null;
        }
        return (
            <Message className={css['file-error-message']} title={t('nacre.ImportController.analysis.header.error')} type="danger">
                {t('ticket.charts.newZone.fileError.seatMap', { format: '.xlsx' })}
            </Message>
        );
    }, [form]);

    const handleDisplayStage = React.useCallback(() => {
        setFormObject({ ...form, seatlib_data: { ...form.seatlib_data, show_stage: !form.seatlib_data.show_stage } });
    }, [setFormObject, form]);

    const handleDisplayColors = React.useCallback(() => {
        setFormObject({ ...form, seatlib_data: { ...form.seatlib_data, display_category_colors: !form.seatlib_data.display_category_colors } });
    }, [setFormObject, form]);

    if (editMode && !isGA) {
        return (
            <ConfigSeatsFrame
                seatMapError={seatMapError}
                handleDisplayStage={handleDisplayStage}
                handleDisplayColors={handleDisplayColors}
                handleSeatMap={handleSeatMap}
                {...props}
            />
        );
    }
    return <SeatsFrame seatMapError={seatMapError} handleDisplayStage={handleDisplayStage} handleDisplayColors={handleDisplayColors} handleSeatMap={handleSeatMap} {...props} />;
}

export function SeatsFrame({
    isGA,
    form,
    setForm,
    editMode,
    inputError,
    categories,
    contingents,
    submitting,
    seatMapError,
    loadingCategories,
    loadingContingents,
    handleType = () => {},
    inputValidity = () => {},
    handleSeatMap = () => {},
    handleDisplayStage = () => {},
    handleDisplayColors = () => {}
}) {
    const { t } = useTranslation();

    const categoriesOptions = React.useMemo(() => {
        if (!categories?.length) {
            return [];
        }
        return categories
            .filter(cat => (form.type === 'general_admission' ? cat.general_admission : !cat.general_admission))
            .map(cat => ({
                text: cat.name,
                value: cat.uuid
            }));
    }, [form, categories]);

    const contingentsOptions = React.useMemo(() => {
        if (!contingents?.length) {
            return [];
        }
        return contingents.map(contingent => ({
            text: contingent.name,
            value: contingent.uuid
        }));
    }, [form, contingents]);

    const onChangeUnlimitedCapacity = React.useCallback((e, { checked }) => {
        setForm('capacityGA', checked ? null : 0);
    });

    return (
        <FrameCard title={t('ticket.charts.zone.categoryTitle')}>
            <LoadingCard loading={loadingCategories || loadingContingents || submitting}>
                <TypeSelector value="seated" form={form} onChange={handleType} />
                <TypeSelector value="general_admission" form={form} onChange={handleType} />

                <div className={css['zone-options-block']}>
                    <SelectLabel
                        label={t('ticket.common.seating.category')}
                        placeholder={t('ticket.rates.category-placeholder')}
                        value={form.category}
                        options={categoriesOptions}
                        onClear={() => setForm('category', null)}
                        onChange={cat => {
                            setForm('category', cat);
                        }}
                        useNew
                    />
                    {!isGA ? (
                        <>
                            <SelectLabel
                                label={t('ticket.common.seating.contingent')}
                                placeholder={t('ticket.rates.contingent-placeholder')}
                                value={form.contingent}
                                options={contingentsOptions}
                                onClear={() => setForm('contingent', null)}
                                onChange={contingent => {
                                    setForm('contingent', contingent);
                                }}
                                useNew
                                disabled={editMode}
                            />
                            {form.seatMap.length ? (
                                <div className={css['file-input-block']}>
                                    <InputLabel label={t('ticket.charts.newZone.excelFile')} />
                                    <UploadPreviewFile onDelete={() => setForm('seatMap', [])} fileSrc={form.seatMap[0]?.path} isUploadedS3={false} displayDate={false} />
                                </div>
                            ) : (
                                <div className={css['seat-map-label']}>
                                    <div className={css['seat-map-label-title']}>{t('ticket.charts.newZone.excelFile')}</div>
                                    <UploadDropZone
                                        acceptedFileExtensions={['.xlsx']}
                                        onFileSelected={(files, rejected) => handleSeatMap(rejected, files[0])}
                                        label={t('ticket.charts.newZone.uploadLabel')}
                                    />
                                    {seatMapError}
                                </div>
                            )}
                            <Form.Field>
                                <InputLabel label={t('attendee.access')} />
                                <Input
                                    errors={Boolean(!inputValidity(form.access))}
                                    value={form.access}
                                    placeholder={t('ticket.charts.newZone.placeholder')}
                                    onChange={({ target: { value } }) => setForm('access', value.trim())}
                                />
                                <Form.FieldErrors errors={[inputError(form.access)]} />
                            </Form.Field>

                            <SelectLabel
                                label={t('ticket.charts.zone.stageLocation')}
                                value={form.scene_location}
                                options={[
                                    { text: t('ticket.charts.zone.locations.up'), value: 'UP' },
                                    { text: t('ticket.charts.zone.locations.down'), value: 'DOWN' }
                                ]}
                                onChange={value => {
                                    setForm('scene_location', value);
                                }}
                                useNew
                            />

                            <ToggleLabel inlinePosition="left" checked={form.seatlib_data.show_stage} label={t('ticket.charts.zone.displayStage')} onChange={handleDisplayStage} />
                            <SelectLabel
                                label={t('ticket.charts.zone.default_label')}
                                value={form.automatic_seat_allocation_strategy}
                                options={[
                                    { text: t('ticket.charts.zone.default_from_top'), value: 'default_from_top' },
                                    { text: t('ticket.charts.zone.default_from_bottom'), value: 'default_from_bottom' }
                                ]}
                                onChange={value => {
                                    setForm('automatic_seat_allocation_strategy', value);
                                }}
                                useNew
                            />
                        </>
                    ) : (
                        <div className={css['capacity-block']}>
                            <Form.Label label={t('ticket.chart.capacity')} optionalText={t('ticket.common.labelOptional')} />
                            <div className={css['container-info-capacity']}>
                                <Counter
                                    numberDecimals={0}
                                    min={0}
                                    max={100000}
                                    onChange={(e, value) => setForm('capacityGA', value)}
                                    disabled={form.capacityGA === null}
                                    step={1}
                                    value={form.capacityGA}
                                />
                                <CheckboxLabel
                                    onChange={onChangeUnlimitedCapacity}
                                    checked={form.capacityGA === null}
                                    label={t(`ticket.chart.capacity_unlimited`)}
                                    name={'capacity_unlimited'}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className={css['colors-block']}>
                    <ToggleLabel
                        inlinePosition="left"
                        checked={form.seatlib_data.display_category_colors}
                        label={t('ticket.charts.newZone.displayColor')}
                        onChange={handleDisplayColors}
                    />
                </div>
            </LoadingCard>
        </FrameCard>
    );
}

export function GeneralFrame({
    form,
    inputError,
    chart,
    zones,
    setForm,
    defaultLanguage,
    languageOptions,
    selectedNameLanguage,
    setSelectedNameLanguage,
    selectedDescriptionLanguage,
    setSelectedDescriptionLanguage,
    submitting,
    loadingzones,
    organization,
    setFormObject,
    zoneId = null,
    inputValidity = () => {}
}) {
    const { t } = useTranslation();

    const [toggleZoneEditModal, setToggleZoneEditModal] = React.useState(false);
    // make zonetree containing zones and their children
    const zonesTree = React.useMemo(() => {
        if (!zones || !zones.length) {
            return [];
        }
        const _zones = [...zones];
        _zones?.map(zone => {
            if (!zone.parent) {
                return;
            }

            let parent_zone = zones.find(z => z.pk === zone.parent);
            parent_zone.children = parent_zone.children || [];
            let has_child = parent_zone.children.find(z => z.pk === zone.pk);
            if (!has_child) {
                parent_zone.children.push(zone);
            }
            return parent_zone;
        });
        return _zones;
    }, [zones]);

    // Recursive function to find all sub-zones of a zone
    const findDescendants = React.useCallback((zone, descendants = []) => {
        zone.children?.forEach(child => {
            descendants.push(child.pk); // Add the child's PK to the list
            findDescendants(child, descendants); // Recurse into the child
        });
        return descendants;
    }, []);

    // Generate parent options for the current zone
    const zoneOptions = React.useMemo(() => {
        if (!zones?.length) {
            return [];
        }
        // If new zone no need to check parent options
        if (!zoneId) {
            return zones.map(potentialParent => ({
                text: potentialParent.name,
                value: potentialParent.uuid
            }));
        }
        // Find the current node in the tree
        const currentNode = zonesTree.find(zone => zone.pk === zoneId);
        if (!currentNode) return [];

        const descendants = findDescendants(currentNode);

        // Exclude the current node and its descendants
        return zones
            .filter(potentialParent => potentialParent.pk !== currentNode.pk && !descendants.includes(potentialParent.pk))
            .map(potentialParent => ({
                value: potentialParent.uuid,
                text: potentialParent.name
            }));
    }, [zoneId, zones, findDescendants]);

    const handleName = React.useCallback(
        value => {
            let trans = form.translations;
            trans[selectedNameLanguage] = value;
            if (selectedNameLanguage !== defaultLanguage) {
                if (!value) {
                    delete trans[selectedNameLanguage];
                }
                setForm('translations', trans);
            } else {
                setFormObject({ ...form, name: value, translations: trans });
            }
        },
        [setFormObject, setForm, form, selectedNameLanguage, defaultLanguage]
    );

    const hasSubZones = React.useMemo(() => {
        return zones?.filter(zn => zn.parent === form.zone?.uuid).length >= 1;
    }, [zones, form]);

    const handleDescription = React.useCallback(
        value => {
            let trans = form.translationsDescription;
            trans[selectedDescriptionLanguage] = value;
            if (selectedDescriptionLanguage !== defaultLanguage) {
                if (!value) {
                    delete trans[selectedDescriptionLanguage];
                }
                setForm('translationsDescription', trans);
            } else {
                setFormObject({ ...form, description: value, translationsDescription: trans });
            }
        },
        [setFormObject, setForm, form, selectedDescriptionLanguage, defaultLanguage]
    );

    const handleZoneEditor = React.useCallback(() => {
        setToggleZoneEditModal(currentState => !currentState);
    }, []);

    return (
        <>
            <DeletionModal
                onClose={() => setFormObject({ ...form, deletingImage: null })}
                onAccept={() => {
                    setFormObject({
                        ...form,
                        image: null,
                        deletingImage: null
                    });
                }}
                icon={null}
                objectToDelete={form.deletingImage}
            />
            <FrameCard title={t('ticket.charts.zone.nameTitle')}>
                <LoadingCard loading={submitting || loadingzones}>
                    <Form.Field>
                        <div className={css['language-label']}>
                            <InputLabel label={<span>{t('ticket.charts.newZone.zoneName')}</span>} />
                            <TranslateDropdown
                                defaultValue={defaultLanguage}
                                languages={languageOptions}
                                onChange={value => setSelectedNameLanguage(value)}
                                selected={Object.keys(form.translations)}
                            />
                        </div>
                        <Input
                            value={!Object.keys(form.translations).length || selectedNameLanguage === defaultLanguage ? form.name : form.translations[selectedNameLanguage]}
                            placeholder="text"
                            onChange={({ target: { value } }) => handleName(value)}
                        />
                    </Form.Field>

                    <Form.Field>
                        <div className={css['zone-description']}>
                            <Form.Label label={t('ticket.charts.newZone.zoneDescription')} optionalText={t('ticket.common.labelOptional')} />
                            <TranslateDropdown
                                defaultValue={defaultLanguage}
                                languages={languageOptions}
                                onChange={value => setSelectedDescriptionLanguage(value)}
                                selected={Object.keys(form.translationsDescription)}
                            />
                        </div>

                        <Input
                            value={
                                !Object.keys(form.translationsDescription).length || selectedDescriptionLanguage === defaultLanguage
                                    ? form.description
                                    : form.translationsDescription[selectedDescriptionLanguage]
                            }
                            placeholder="text"
                            onChange={({ target: { value } }) => handleDescription(value)}
                        />
                    </Form.Field>

                    <Form.Label label={t('ticket.charts.newZone.parentZone')} optionalText={t('ticket.common.labelOptional')} />
                    <Select
                        placeholder={t('partners.common.seating.grandstand')}
                        options={zoneOptions}
                        value={form.parent}
                        onChange={cat => {
                            setForm('parent', cat);
                        }}
                        onClear={() => setForm('parent', null)}
                        useNew
                    />

                    {chart && !chart.pacifa_key && (
                        <div className={css['zone-representation-block']}>
                            <ToggleContainer
                                toggle={form.zone_representation}
                                onToggle={value => setForm('zone_representation', value)}
                                title={
                                    <span>
                                        {t('ticket.charts.newZone.zoneImage')}
                                        <TooltipHelp title={t(`ticket.charts.newZone.representation_tooltip`)} />
                                    </span>
                                }
                            >
                                <Section>
                                    <div className={css['seat-map-label']}>
                                        <ZoneEditorModal
                                            organization={organization}
                                            zones={zones}
                                            zone={form.zone}
                                            chart={chart}
                                            open={toggleZoneEditModal}
                                            onClose={handleZoneEditor}
                                        />
                                        <UploadFileZone
                                            organization={organization}
                                            onUpload={image => {
                                                setFormObject({
                                                    ...form,
                                                    preview_picture_id: image.uuid,
                                                    image: image.url
                                                });
                                            }}
                                            fileTypes={['.jpg', '.png']}
                                            labelDropzone={t('ticket.event.dropzone_label')}
                                            subLabelDropzone={t('ticket.charts.newZone.maxSize', { size: '2Mo' })}
                                            maxSize={2000000}
                                            maxPixels={2400 * 2400}
                                            file={form.image}
                                            onDelete={synth => {
                                                synth.preventDefault();
                                                setFormObject({
                                                    ...form,
                                                    deletingImage: true
                                                });
                                            }}
                                        />
                                        {hasSubZones && <ButtonZoneEditorAccess image={form.image} setEditChart={setToggleZoneEditModal} />}
                                    </div>
                                </Section>
                            </ToggleContainer>
                        </div>
                    )}
                </LoadingCard>
            </FrameCard>
        </>
    );
}

export function OptionalFrame({ submitting, form, setForm, setFormObject }) {
    const { t } = useTranslation();

    const handleDisplayUnavailable = React.useCallback(
        value => {
            setFormObject({ ...form, seatlib_data: { ...form.seatlib_data, display_unavailable: value } });
        },
        [setFormObject, form]
    );

    return (
        <OptionalForm labelClosed={t('ticket.charts.newZone.additionnal.hide_additionnal')} labelOpen={t('ticket.charts.newZone.additionnal.display_additionnal')} open={false}>
            <FrameCard title={t('ticket.charts.newZone.additionnal.title')}>
                <LoadingCard loading={submitting}>
                    <div className={css['display-block']}>
                        <CounterLabel
                            label={t('ticket.charts.newInventory.display_order')}
                            numberDecimals={0}
                            min={0}
                            onChange={(e, value) => setForm('display_order', value)}
                            step={1}
                            value={form.display_order}
                        />
                    </div>
                    <ToggleLabel
                        inlinePosition="left"
                        checked={form.seatlib_data.display_unavailable}
                        label={
                            <span>
                                {t('ticket.charts.zone.displayWhenUnavailable')}
                                <TooltipHelp title={t(`ticket.charts.newZone.display_tooltip`)} />
                            </span>
                        }
                        onChange={({ target: { checked } }) => handleDisplayUnavailable(checked)}
                    />
                </LoadingCard>
            </FrameCard>
        </OptionalForm>
    );
}

export function ConfigSeatsFrame({
    form,
    chart,
    zoneId,
    setForm,
    chartId,
    synczones,
    handleType,
    submitting,
    categories,
    contingents,
    organization,
    seatMapError,
    loadingzones,
    handleSeatMap = () => {},
    handleDisplayStage = () => {},
    handleDisplayColors = () => {},
    inputError,
    inputValidity = () => {}
}) {
    const { t } = useTranslation();
    const [modal, setModal] = React.useState(false);

    const handleToggle = React.useCallback(() => {
        setModal(currentState => !currentState);
    }, []);

    const [showUpdateModal, setShowUpdateModal] = React.useState(false);

    return (
        <FrameCard title={t('ticket.charts.zone.categoryTitle')}>
            <LoadingCard loading={submitting}>
                <TypeSelector id={'std'} value={'seated'} form={form} onChange={handleType} />
                <TypeSelector id={'ga'} value={'general_admission'} form={form} onChange={handleType} />
                <div className={css['zone-options-block']}>
                    {chart && !chart.pacifa_key && (
                        <>
                            <SelectLabel
                                label={t('ticket.charts.zone.stageLocation')}
                                value={form.scene_location}
                                options={[
                                    { text: t('ticket.charts.zone.locations.up'), value: 'UP' },
                                    { text: t('ticket.charts.zone.locations.down'), value: 'DOWN' }
                                ]}
                                onChange={value => {
                                    setForm('scene_location', value);
                                }}
                                useNew
                            />
                            <ToggleLabel inlinePosition="left" checked={form.seatlib_data.show_stage} label={t('ticket.charts.zone.displayStage')} onChange={handleDisplayStage} />
                            <SelectLabel
                                label={t('ticket.charts.zone.default_label')}
                                value={form.automatic_seat_allocation_strategy}
                                options={[
                                    { text: t('ticket.charts.zone.default_from_top'), value: 'default_from_top' },
                                    { text: t('ticket.charts.zone.default_from_bottom'), value: 'default_from_bottom' }
                                ]}
                                onChange={value => {
                                    setForm('automatic_seat_allocation_strategy', value);
                                }}
                                useNew
                            />
                        </>
                    )}
                    <ToggleLabel
                        inlinePosition="left"
                        checked={form.seatlib_data.display_category_colors}
                        label={t('ticket.charts.newZone.displayColor')}
                        onChange={handleDisplayColors}
                    />
                </div>
                {form.seat_count >= 1 ? (
                    <div className={css['config-button-container']}>
                        <Button label={t('ticket.charts.zone.seatmap')} onClick={handleToggle} primary inverted />
                        <Button label={t('ticket.charts.newZone.update.cta')} onClick={() => setShowUpdateModal(true)} primary inverted />
                    </div>
                ) : (
                    <div className={css['seat-map-label']}>
                        {chart && !chart.pacifa_key && !zoneId && (
                            <>
                                <div className={css['seat-map-label-title']}>{t('ticket.charts.newZone.excelFile')}</div>
                                <UploadDropZone
                                    acceptedFileExtensions={['.xlsx']}
                                    onFileSelected={(files, rejected) => handleSeatMap(rejected, files[0])}
                                    label={t('ticket.charts.newZone.uploadLabel')}
                                />
                                {seatMapError}
                            </>
                        )}
                    </div>
                )}
                <SeatMapModal
                    toggle={modal}
                    zoneId={zoneId}
                    zone={form.zone}
                    setForm={setForm}
                    chartId={chartId}
                    synczones={synczones}
                    onToggle={handleToggle}
                    loadingzones={loadingzones}
                    organization={organization}
                    categories={categories?.filter(cat => !cat.general_admission)}
                    contingents={contingents}
                />

                <UpdateZoneModal
                    isOpen={showUpdateModal}
                    onClose={() => setShowUpdateModal(false)}
                    categories={categories?.filter(cat => !cat.general_admission)}
                    contingents={contingents}
                    chartId={chartId}
                    zone={form.zone}
                    synczones={synczones}
                    inputError={inputError}
                    inputValidity={inputValidity}
                />
            </LoadingCard>
        </FrameCard>
    );
}
